import { useCallback, useEffect, useRef, useState } from 'react'

const INTERACTIVE_ELEMENTS_SELECTOR = 'button, a, input, label, textarea, select'

export const useMobileSheetDragToClose = ({
  isDesktop,
  isOpen,
  onCloseImmediate,
  closeDurationMs = 260,
  dragThresholdPx = 6,
  closeThresholdPx = 2,
  ignoreSelector,
}) => {
  const [sheetOffsetY, setSheetOffsetY] = useState(null)
  const [isSheetClosing, setIsSheetClosing] = useState(false)
  const [isSheetDragging, setIsSheetDragging] = useState(false)

  const sheetCloseTimerRef = useRef(null)
  const capturedElementRef = useRef(null)
  const sheetDragRef = useRef({
    startY: 0,
    pointerId: null,
    isTracking: false,
    isDragging: false,
  })

  const releaseCapturedPointer = useCallback(() => {
    const el = capturedElementRef.current
    const pointerId = sheetDragRef.current.pointerId

    if (!el || pointerId === null || pointerId === undefined) return

    try {
      el.releasePointerCapture(pointerId)
    } catch (_) {
      // ignore
    }

    capturedElementRef.current = null
  }, [])

  const resetDragState = useCallback(() => {
    releaseCapturedPointer()

    sheetDragRef.current = {
      startY: 0,
      pointerId: null,
      isTracking: false,
      isDragging: false,
    }

    setIsSheetDragging(false)
    setSheetOffsetY(0)
  }, [releaseCapturedPointer])

  const closeFormImmediately = useCallback(() => {
    onCloseImmediate?.()
  }, [onCloseImmediate])

  const requestCloseMobileSheet = useCallback(() => {
    if (isDesktop) {
      closeFormImmediately()
      return
    }

    if (!isOpen) return
    if (isSheetClosing) return

    setIsSheetClosing(true)
    setIsSheetDragging(false)
    setSheetOffsetY(window.innerHeight)

    if (sheetCloseTimerRef.current) clearTimeout(sheetCloseTimerRef.current)
    sheetCloseTimerRef.current = setTimeout(() => {
      closeFormImmediately()
      setIsSheetClosing(false)
      setSheetOffsetY(null)
    }, closeDurationMs)
  }, [closeDurationMs, closeFormImmediately, isDesktop, isOpen, isSheetClosing])

  useEffect(() => {
    return () => {
      if (sheetCloseTimerRef.current) clearTimeout(sheetCloseTimerRef.current)
    }
  }, [])

  useEffect(() => {
    const handleBlur = () => {
      if (!sheetDragRef.current.isTracking) return
      resetDragState()
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return
      if (!sheetDragRef.current.isTracking) return
      resetDragState()
    }

    window.addEventListener('blur', handleBlur)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('blur', handleBlur)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [resetDragState])

  const handleSheetTransitionEnd = useCallback(() => {
    if (isSheetClosing) return
    if (isSheetDragging) return
    if (sheetOffsetY === 0) setSheetOffsetY(null)
  }, [isSheetClosing, isSheetDragging, sheetOffsetY])

  const handleSheetHeaderPointerDown = useCallback(
    (e) => {
      if (e.target?.closest?.(INTERACTIVE_ELEMENTS_SELECTOR)) return
      if (ignoreSelector && e.target?.closest?.(ignoreSelector)) return

      sheetDragRef.current = {
        startY: e.clientY,
        pointerId: e.pointerId,
        isTracking: true,
        isDragging: false,
      }

      try {
        e.currentTarget.setPointerCapture(e.pointerId)
        capturedElementRef.current = e.currentTarget
      } catch (_) {
        // ignore
      }
    },
    [ignoreSelector]
  )

  const handleSheetHeaderPointerMove = useCallback(
    (e) => {
      const st = sheetDragRef.current
      if (!st.isTracking || st.pointerId !== e.pointerId) return

      const dy = e.clientY - st.startY

      if (!st.isDragging) {
        if (Math.abs(dy) < dragThresholdPx) return
        st.isDragging = true
        st.startY = e.clientY
        setIsSheetDragging(true)
        setSheetOffsetY(0)
      }

      const nextY = Math.max(0, e.clientY - st.startY)
      setSheetOffsetY(nextY)

      e.preventDefault()
    },
    [dragThresholdPx]
  )

  const handleSheetHeaderPointerEnd = useCallback(
    (e) => {
      const st = sheetDragRef.current
      if (!st.isTracking || st.pointerId !== e.pointerId) return

      releaseCapturedPointer()

      if (!st.isDragging) {
        st.isTracking = false
        st.pointerId = null
        return
      }

      const dy = e.clientY - st.startY

      st.isTracking = false
      st.pointerId = null
      st.isDragging = false

      setIsSheetDragging(false)

      if (dy > closeThresholdPx) {
        requestCloseMobileSheet()
        return
      }

      setSheetOffsetY(0)
    },
    [closeThresholdPx, releaseCapturedPointer, requestCloseMobileSheet]
  )

  return {
    sheetOffsetY,
    isSheetClosing,
    isSheetDragging,
    requestCloseMobileSheet,
    handleSheetTransitionEnd,
    handleSheetHeaderPointerDown,
    handleSheetHeaderPointerMove,
    handleSheetHeaderPointerEnd,
  }
}
